import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight, RefreshCw } from "lucide-react";

const PERKS = [
  "Browse every HausCard in the Haus",
  "Search & filter by role, genre, location & remote",
  "A real industry brief to write to, every week",
  "See who's open to collaborate",
  "Request intros referencing the exact song you heard",
];

export default function Paywall() {
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("annual");
  const [checking, setChecking] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const refreshAccess = async () => {
    setChecking(true);
    try {
      const res = await api.post("/billing/refresh", {});
      if (res.data.tier === "paid") {
        await refresh();
        toast.success("You're in — access confirmed. Welcome to the Haus!");
        navigate("/directory");
      } else {
        toast.error(`No payment found for ${res.data.email}. Access is tied to the email used at checkout — if you paid with a different email, message us and we'll link it up.`, { duration: 9000 });
      }
    } catch { toast.error("Could not check right now — try again in a minute"); }
    finally { setChecking(false); }
  };

  const checkout = async (selectedPlan) => {
    setBusy(true);
    try {
      const res = await api.post("/billing/checkout", { plan: selectedPlan });
      toast("Redirecting to secure Kajabi checkout…");
      window.open(res.data.checkout_url, "_blank");
    } catch { toast.error("Could not start checkout"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-ink">
      <Header />
      <div className="max-w-[900px] mx-auto px-5 md:px-10 pt-28 pb-20">
        <div className="text-center mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Membership</span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase text-cream mt-3">Unlock the Haus</h1>
          <p className="text-ash mt-4 max-w-md mx-auto">Your card is already live and free. To browse and connect with other creators, unlock full access.</p>
        </div>

        <div className="max-w-[520px] mx-auto border border-line">
          {/* Paid plans */}
          <div className="bg-surface p-8 md:p-10 flex flex-col">
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-ash">WriterHaus Access</span>

            {/* Plan toggle */}
            <div className="grid grid-cols-2 gap-px bg-line border border-line mt-5" data-testid="plan-toggle">
              <button
                onClick={() => setPlan("annual")}
                data-testid="plan-annual-btn"
                className={`relative p-4 pt-7 text-left transition-colors ${plan === "annual" ? "bg-gold text-ink" : "bg-ink text-cream hover:text-gold"}`}
              >
                <span className="absolute top-1.5 right-1.5 font-mono text-[9px] font-bold uppercase tracking-wider bg-ink text-gold px-1.5 py-0.5">Save 38%</span>
                <span className="font-mono text-[10px] uppercase tracking-wide opacity-80">Annual</span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="font-heading text-3xl font-bold tracking-tighter">$59</span>
                  <span className="font-mono text-[10px] uppercase">/ yr</span>
                </div>
              </button>
              <button
                onClick={() => setPlan("monthly")}
                data-testid="plan-monthly-btn"
                className={`p-4 pt-7 text-left transition-colors ${plan === "monthly" ? "bg-gold text-ink" : "bg-ink text-cream hover:text-gold"}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-wide opacity-80">Monthly</span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="font-heading text-3xl font-bold tracking-tighter">$8</span>
                  <span className="font-mono text-[10px] uppercase">/ mo</span>
                </div>
              </button>
            </div>

            <p className="font-mono text-[11px] uppercase tracking-wide text-ash mt-4 mb-6 min-h-[2.5rem]" data-testid="plan-summary">
              {plan === "annual"
                ? "$59 billed yearly — just $4.92/mo. Auto-renews after 12 months."
                : "$8 billed monthly. Cancel anytime."}
            </p>

            <ul className="flex flex-col gap-3 mb-8 flex-1">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm text-cream"><Check size={16} className="text-gold mt-0.5 shrink-0" /> {perk}</li>
              ))}
            </ul>
            <button onClick={() => checkout(plan)} disabled={busy} data-testid="kajabi-checkout-btn" className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-sm py-4 hover:bg-gold-hi transition-colors disabled:opacity-60">
              {plan === "annual" ? "Unlock for $59/yr" : "Unlock for $8/mo"} <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <div className="max-w-[520px] mx-auto mt-8 border border-line bg-surface p-5 text-center">
          <p className="text-cream text-sm font-medium mb-1">Already paid?</p>
          <p className="text-ash text-xs leading-relaxed mb-4">Access unlocks automatically after checkout. If you're still seeing this page, tap below — your access is tied to the email you used at checkout.</p>
          <button onClick={refreshAccess} disabled={checking} data-testid="refresh-access-btn" className="inline-flex items-center gap-2 border border-gold text-gold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            <RefreshCw size={14} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "I already paid — refresh my access"}
          </button>
        </div>

        <p className="text-center font-mono text-[11px] uppercase tracking-wide text-ash/60 mt-8">Billing handled securely via Kajabi · cancel anytime</p>
      </div>
    </div>
  );
}
